import cn from 'classnames';
import { useState } from 'react';
import { Button, lucideIcons, Tooltip } from '@round/creatorbase-ui';
import useClientSelect from 'Modules/Plans/Clients/hooks/useClientSelect';
import { OutreachRequestsFilters } from '../components/OutreachRequestsFilters/OutreachRequestsFilters';
import { getHasActiveFilters } from '../components/OutreachRequestsFilters/helpers';
import { CreateOutreachRequestModal } from './components/CreateOutreachRequestModal/CreateOutreachRequestModal';
import { OutreachRequestsEmptyState } from './components/OutreachRequestsEmptyState/OutreachRequestsEmptyState';
import { getEmptyStateVariant } from './components/OutreachRequestsEmptyState/helpers';
import { OutreachRequestsTable } from './components/OutreachRequestsTable/OutreachRequestsTable';
import { getOrdering, getSorting } from './components/OutreachRequestsTable/helpers';
import { useOutreachRequestsList } from './hooks/useOutreachRequestsList';
import { mapToApiParams, useOutreachRequestsUrlState } from './hooks/useOutreachRequestsUrlState';
import styles from './OutreachRequestsList.module.css';

const { Plus, TriangleAlert } = lucideIcons;

const NO_CLIENT_TOOLTIP = 'Select a client first';
const LOAD_MORE_ERROR_TOOLTIP = 'Failed to load more data, you can reload';

export const OutreachRequestsList = () => {
    const { selectedClientId } = useClientSelect();
    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const { urlState, setUrlState } = useOutreachRequestsUrlState();
    // Filters and sorting are part of the query key, so changing either starts a fresh query at page 1.
    const {
        rows,
        isLoading,
        hasNextPage,
        isFetchingNextPage,
        isFetchNextPageError,
        fetchNextPage,
        isError,
        errorMessage,
        audiosById,
        getIsAudiosLoading,
        countriesById,
        isLoadingCountries,
    } = useOutreachRequestsList({
        client_id: selectedClientId,
        ...mapToApiParams(urlState),
    });

    const hasSelectedClient = typeof selectedClientId === 'number';

    return (
        <div className={cn(styles.container, 'twp')}>
            <header className={cn(styles.section, styles.header)}>
                <div className={styles.titleGroup}>
                    <h1 className={styles.title}>Outreach Requests</h1>

                    {/* The rows already loaded stay on screen, so the failure is reported beside them. */}
                    {isFetchNextPageError && (
                        <Tooltip>
                            <Tooltip.Trigger asChild>
                                <span className={styles.loadMoreError} role="img" aria-label={LOAD_MORE_ERROR_TOOLTIP}>
                                    <TriangleAlert />
                                </span>
                            </Tooltip.Trigger>
                            <Tooltip.Content>{LOAD_MORE_ERROR_TOOLTIP}</Tooltip.Content>
                        </Tooltip>
                    )}
                </div>

                <Tooltip>
                    {/* A disabled button swallows pointer events, so the trigger sits on a wrapper. */}
                    <Tooltip.Trigger asChild>
                        <span className={styles.createTrigger}>
                            <Button size="xs" disabled={!hasSelectedClient} onClick={() => setIsCreateOpen(true)}>
                                <Plus /> Request
                            </Button>
                        </span>
                    </Tooltip.Trigger>
                    {!hasSelectedClient && <Tooltip.Content>{NO_CLIENT_TOOLTIP}</Tooltip.Content>}
                </Tooltip>
            </header>

            <section className={styles.section}>
                <OutreachRequestsFilters clientId={selectedClientId} filters={urlState} onFiltersChange={setUrlState} />
            </section>

            <section className={cn(styles.section, styles.tableSection)}>
                <OutreachRequestsTable
                    rows={rows}
                    meta={{ isLoading, audiosById, getIsAudiosLoading, countriesById, isLoadingCountries }}
                    sorting={getSorting(urlState.ordering)}
                    onSortingChange={(sorting) => setUrlState({ ordering: getOrdering(sorting) })}
                    fetchNextPage={() => {
                        // Scrolling on past a page that failed would retry it on every scroll event.
                        if (hasNextPage && !isFetchingNextPage && !isFetchNextPageError) {
                            void fetchNextPage();
                        }
                    }}
                    noDataLabel={
                        <OutreachRequestsEmptyState
                            variant={getEmptyStateVariant({
                                hasSelectedClient,
                                hasFilters: getHasActiveFilters(urlState),
                                isError,
                            })}
                            errorMessage={errorMessage}
                            onCreateRequest={() => setIsCreateOpen(true)}
                        />
                    }
                />
            </section>

            <CreateOutreachRequestModal isOpen={isCreateOpen} onClose={() => setIsCreateOpen(false)} />
        </div>
    );
};
